'use client';

import React, { useEffect, useTransition, type ReactNode } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTranslation } from 'react-i18next';

import { analytics } from '@kit/analytics';
import { DancerAvatar } from '@kit/artists';
import {
  CONDITION_LABEL_KEY,
  formatListingPrice,
} from '@kit/marketplace/labels';
import type { ListingWithImages } from '@kit/marketplace/types';
import {
  dancerMarketplacePath,
  marketplaceSellerPath,
} from '@kit/shared/handles';
import { Badge } from '@kit/ui/badge';
import { Button } from '@kit/ui/button';
import { Separator } from '@kit/ui/separator';
import { Trans } from '@kit/ui/trans';
import { VerifiedSellerBadge } from '@kit/ui/verified-seller-badge';

import { initiateCheckoutAction } from '../[id]/_lib/server/initiate-checkout.action';
import { MessageSellerButton } from './chat/message-seller-button';
import { ListingImageGallery } from './listing-image-gallery';

// Lazy, client-only: keeps mapbox-gl (~200 KB gz) out of the initial bundle —
// it loads only on detail pages that actually have coordinates.
const ListingLocationMap = dynamic(
  () => import('./listing-location-map').then((mod) => mod.ListingLocationMap),
  { ssr: false },
);

interface ListingDetailProps {
  listing: ListingWithImages;
  /** Pre-resolved URLs in the same order as listing.images. */
  imageUrls: string[];
  /**
   * Verified-seller signal — pre-computed server-side by
   * `SellerService.getVerifiedSellerStatus(listing.seller_id)`. When true,
   * we render a small badge near the seller-action buttons.
   */
  sellerVerified?: boolean;
  /** Tooltip text from the same service call; falls back to a default. */
  sellerVerifiedReason?: string;
  /**
   * Display info for the "Sold by" row — avatar + name + optional link
   * to the dancer profile. Pre-resolved server-side by
   * `SellerService.getSellerDisplayInfo(listing.seller_id)`. Pass `null`
   * if the lookup failed; the row will not render.
   */
  seller?: {
    sellerId: string;
    displayName: string;
    firstName: string | null;
    lastName: string | null;
    primaryHeadshotUrl: string | null;
    profilePhotoUrl: string | null;
    /**
     * The seller's handle whenever they have one (public or private).
     * When non-null, the avatar + name link to their always-public
     * marketplace storefront at `/<handle>/marketplace`; otherwise they
     * link to the UUID-keyed `/marketplace/seller/<sellerId>`.
     */
    marketplaceHandle: string | null;
  } | null;
  /**
   * When false (current default), the marketplace runs in free /
   * contact-seller mode: hide the Buy button and the Connect-based
   * verified badge, and promote "Message seller" to the primary CTA.
   */
  commerceEnabled?: boolean;
  /**
   * Auth-gated report control, rendered by the server page so the
   * anon ↔ auth decision (IfAuthenticated) stays server-side. Anon
   * visitors get a sign-in link instead of a dialog that would throw.
   */
  reportSlot?: ReactNode;
  /**
   * Seller reputation control (rating badge → reviews drawer → write-review
   * dialog), rendered by the server page so seller stats + the viewer's
   * review-eligibility are resolved server-side. See
   * `seller-reviews-launcher.tsx`.
   */
  reviewsSlot?: ReactNode;
  /**
   * Whether the current viewer is signed in. Drives the "Message seller"
   * button: authed → opens the floating chat with the seller; anon → sign-in
   * link. Resolved server-side by the detail page.
   */
  viewerIsAuthenticated?: boolean;
  /**
   * The viewer's own id (profiles.id), when signed in. Hides "Message seller"
   * on the viewer's OWN listing — you can't message yourself (BAL-305).
   */
  viewerId?: string | null;
  /**
   * Capability-gated Manage/Edit affordance (nav IA Phase C), rendered by the
   * server page: it resolves the viewer's management surface via
   * `listingManageHref` (super-admin → /admin/shops, owner of an editable
   * listing → /marketplace/<slug>/edit) and passes an `<EntityManageAction>`
   * — or nothing (absent, not disabled). Replaces this component's previous
   * inline owner-edit logic so the permission check lives server-side.
   */
  manageSlot?: ReactNode;
}

// Delivery method only — the city moved to the dedicated Location section.
function buildDeliveryLine(
  listing: ListingWithImages,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const parts: string[] = [];
  if (listing.shipping_available)
    parts.push(t('explore:listingDetail.shipping'));
  if (listing.pickup_available) parts.push(t('explore:listingDetail.pickup'));
  return parts.join(' · ');
}

/**
 * "Lausanne, Switzerland" — country name resolved from the ISO code. Legacy
 * rows without structured location fall back to the free-text pickup_city.
 */
function buildLocationLabel(listing: ListingWithImages): string | null {
  const city = listing.location_city ?? listing.pickup_city;
  if (!city) return null;
  if (listing.location_city && listing.location_country_code) {
    try {
      const country = new Intl.DisplayNames(['en'], { type: 'region' }).of(
        listing.location_country_code.toUpperCase(),
      );
      if (country && country !== listing.location_country_code.toUpperCase()) {
        return `${city}, ${country}`;
      }
    } catch {
      // Malformed region code — fall through to the bare city.
    }
  }
  return city;
}

/**
 * Client component — activates the Buy button with Stripe Connect checkout.
 * Phase 1d: Buy button now calls initiateCheckoutAction server action.
 */
export function ListingDetail({
  listing,
  imageUrls,
  sellerVerified = false,
  sellerVerifiedReason,
  seller = null,
  commerceEnabled = false,
  reportSlot = null,
  reviewsSlot = null,
  viewerIsAuthenticated = false,
  viewerId = null,
  manageSlot = null,
}: ListingDetailProps) {
  const { t } = useTranslation();
  const isOwnListing = Boolean(viewerId) && viewerId === listing.seller_id;
  // BAL-591: a brand listing links OUT to the seller's own shop. The primary
  // CTA becomes "Visit shop"; in-app Buy / Message-seller are suppressed since
  // the purchase happens off-platform (link-out model, BAL-598 Q1=A).
  const isBrandLinkout =
    listing.seller_type === 'brand' && Boolean(listing.shop_url);
  const [isPending, startTransition] = useTransition();
  const [buyError, setBuyError] = React.useState<string | null>(null);
  const pathname = usePathname();

  // PostHog: listing-view — fires once per mount. Properties are
  // non-PII (listing id, category slug, seller verification flag).
  useEffect(() => {
    void analytics.trackEvent('listing-view', {
      listing_id: listing.id,
      listing_category_slug: listing.category_slug,
      seller_is_verified: sellerVerified ? 'true' : 'false',
    });
    // We intentionally exclude `sellerVerified` from the dep array — the badge
    // can flip server-side later but the view event should only fire once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing.id]);

  function handleBuy() {
    setBuyError(null);
    // PostHog: checkout-start — fired when buyer clicks Buy. Captures the
    // intent even when the server action ultimately fails (drop-off funnel).
    void analytics.trackEvent('checkout-start', {
      listing_id: listing.id,
      listing_category_slug: listing.category_slug,
      seller_is_verified: sellerVerified ? 'true' : 'false',
    });
    startTransition(async () => {
      const result = await initiateCheckoutAction(listing.id, pathname);
      // `redirect()` throws — if we reach here the action returned an error.
      if (!result.success) {
        setBuyError(result.error);
      }
    });
  }

  function handleVisitShop() {
    // PostHog: visit-shop — fires when a buyer taps the brand link-out CTA.
    void analytics.trackEvent('visit-shop', {
      listing_id: listing.id,
      listing_category_slug: listing.category_slug,
    });
  }

  function handleMessageSeller() {
    // PostHog: message-seller — fires when buyer clicks the "Message seller"
    // CTA. Tracked separately from listing-view so we can compute
    // view → message conversion per category.
    void analytics.trackEvent('message-seller', {
      listing_id: listing.id,
      listing_category_slug: listing.category_slug,
      seller_is_verified: sellerVerified ? 'true' : 'false',
    });
  }

  // Pair each listing image with its resolved URL. Filter out any with no
  // URL (rare edge case if a non-public bucket signed-URL request failed).
  const galleryImages = listing.images
    .map((image, idx) => ({ image, url: imageUrls[idx] ?? '' }))
    .filter((entry) => entry.url.length > 0);

  const locationLabel = buildLocationLabel(listing);

  return (
    <div
      className="container mx-auto grid gap-8 px-4 py-8 md:grid-cols-2"
      data-testid="listing-detail"
    >
      <ListingImageGallery images={galleryImages} title={listing.title} />

      <div className="space-y-6">
        <div>
          <Badge variant="outline" className="mb-2">
            {t(CONDITION_LABEL_KEY[listing.condition])}
          </Badge>
          <h1 className="text-3xl font-semibold">{listing.title}</h1>
          {listing.brand && (
            <p className="text-muted-foreground mt-1">{listing.brand}</p>
          )}
        </div>

        <div className="text-3xl font-semibold">
          {formatListingPrice(listing.price_cents, listing.currency)}
        </div>

        <Separator />

        <div className="space-y-2">
          {listing.size && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                <Trans i18nKey="explore:listingDetail.size" />
              </span>
              <span>{listing.size}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">
              <Trans i18nKey="explore:listingDetail.delivery" />
            </span>
            <span>{buildDeliveryLine(listing, t)}</span>
          </div>
        </div>

        <Separator />

        <div>
          <h2 className="mb-2 font-medium">
            <Trans i18nKey="common:form.description" />
          </h2>
          <p className="text-muted-foreground whitespace-pre-line">
            {listing.description}
          </p>
        </div>

        {locationLabel && (
          <>
            <Separator />

            <div data-testid="listing-location">
              <h2 className="mb-2 font-medium">
                <Trans i18nKey="explore:listingDetail.locationHeading" />
              </h2>
              <p className="text-sm">{locationLabel}</p>
              <p className="text-muted-foreground mt-1 text-sm">
                <Trans i18nKey="explore:listingDetail.locationApproximate" />
              </p>
              {listing.location_latitude != null &&
                listing.location_longitude != null && (
                  <ListingLocationMap
                    latitude={Number(listing.location_latitude)}
                    longitude={Number(listing.location_longitude)}
                    label={`Approximate area of ${locationLabel}`}
                    className="mt-3 h-48 overflow-hidden rounded-lg border"
                  />
                )}
            </div>
          </>
        )}

        <Separator />

        {/* "Sold by" row — avatar + name ALWAYS link to the seller's
            marketplace profile (every active item they have for sale, not
            just this one). Any dancer with a handle links to their pretty,
            indexed `/<handle>/marketplace` — public OR private, since the
            storefront is a distinct, always-public entity from the dancer
            profile. Non-dancers / handle-less sellers link to the
            UUID-keyed `/marketplace/seller/<sellerId>`. The verified-seller
            badge (Stripe-Connect-based) only shows in commerce mode. */}
        {seller ? (
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="text-muted-foreground">
              <Trans i18nKey="explore:listingDetail.soldBy" />
            </span>
            <Link
              href={
                seller.marketplaceHandle
                  ? dancerMarketplacePath(seller.marketplaceHandle)
                  : marketplaceSellerPath(seller.sellerId)
              }
              className="hover:text-foreground inline-flex items-center gap-2 transition-colors"
              aria-label={t('explore:listingDetail.viewSellerItems', {
                name: seller.displayName,
              })}
            >
              <DancerAvatar
                primaryHeadshotUrl={seller.primaryHeadshotUrl}
                profilePhotoUrl={seller.profilePhotoUrl}
                firstName={seller.firstName}
                lastName={seller.lastName}
                fullName={seller.displayName}
                size="sm"
              />
              <span className="font-medium">{seller.displayName}</span>
            </Link>
            {commerceEnabled && sellerVerified && (
              <VerifiedSellerBadge tooltip={sellerVerifiedReason} />
            )}
          </div>
        ) : (
          commerceEnabled &&
          sellerVerified && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">
                <Trans i18nKey="explore:listingDetail.soldBy" />
              </span>
              <VerifiedSellerBadge tooltip={sellerVerifiedReason} />
            </div>
          )
        )}

        {reviewsSlot && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">
              <Trans i18nKey="explore:listingDetail.sellerRating" />
            </span>
            {reviewsSlot}
          </div>
        )}

        <div className="space-y-2">
          {/* Server-gated Manage/Edit affordance — a seller viewing their own
              listing (or a super-admin) jumps straight to their management
              surface without going via the /home/listings dashboard. Shown
              first so it's the seller's primary action on their own item. */}
          {manageSlot}
          {isBrandLinkout ? (
            /* BAL-591 brand link-out: the whole purchase happens on the
               brand's own shop, so the primary (only) buyer action is an
               external link. New tab + noopener/noreferrer/nofollow. */
            <Button asChild className="w-full" size="lg">
              <a
                href={listing.shop_url ?? '#'}
                target="_blank"
                rel="noopener noreferrer nofollow"
                onClick={handleVisitShop}
              >
                {t('explore:listingDetail.visitShop')}
              </a>
            </Button>
          ) : (
            <>
              {commerceEnabled && (
                <>
                  <Button
                    className="w-full"
                    onClick={handleBuy}
                    disabled={isPending}
                    aria-busy={isPending}
                  >
                    {isPending
                      ? t('explore:listingDetail.redirectingToPayment')
                      : t('explore:listingDetail.buyFor', {
                          price: formatListingPrice(
                            listing.price_cents,
                            listing.currency,
                          ),
                        })}
                  </Button>
                  {buyError && (
                    <p role="alert" className="text-destructive text-sm">
                      {buyError}
                    </p>
                  )}
                </>
              )}
              {/* Message seller opens the existing floating chat bubble pointed
                  at the seller (item-anchored), not a full-page inbox.
                  Anonymous visitors get a sign-in link. */}
              <MessageSellerButton
                sellerId={listing.seller_id}
                listingId={listing.id}
                sellerName={
                  seller?.displayName ?? t('explore:seller.fallbackName')
                }
                sellerAvatarUrl={
                  seller?.profilePhotoUrl ?? seller?.primaryHeadshotUrl
                }
                listingTitle={listing.title}
                isAuthenticated={viewerIsAuthenticated}
                isOwnListing={isOwnListing}
                signInHref={`/auth/sign-in?next=/marketplace/${listing.slug}`}
                variant={commerceEnabled ? 'outline' : 'default'}
                fullWidth
                onActivate={handleMessageSeller}
              />
            </>
          )}
        </div>

        {/* Bundle-C-report-link-start */}
        {reportSlot && (
          <div className="border-border/40 flex justify-center border-t pt-4">
            {reportSlot}
          </div>
        )}
        {/* Bundle-C-report-link-end */}
      </div>
    </div>
  );
}
